import { DBConfig } from 'ngx-indexed-db';

/**
 * Configuração do banco de dados IndexedDB da aplicação.
 *
 * Cada store corresponde a uma "tabela" independente, com seu próprio keyPath
 * e conjunto de índices. Todos os stores seguem o padrão:
 * - `keyPath`: campo identificador único do registro (nunca autoIncrement)
 * - `storeSchema`: índices secundários para consultas por campo específico
 *
 * ## Incremento de versão
 * Sempre que um novo store for adicionado ou um schema for alterado,
 * incremente `version` em +1. O ngx-indexed-db executa a migração automaticamente.
 *
 * Versão atual: **20**
 * - v8:  Stores dedicados por entidade para as antigas tarefas `reference`
 *        (b2b_unit_details, categories, warehouses, payment_methods, etc.)
 * - v9:  Stores `promotions` e `coupons` para motor de promoções offline
 * - v10: (reservada para migrações intermediárias)
 * - v11: Stores `all_b2bunits_hierarchy` e `b2bunit_addresses`
 * - v12-v16: (migrações intermediárias)
 * - v17: Remove `b2b_unit_details` e `all_b2bunits_hierarchy`;
 *        adiciona `all_b2bunits` (dados completos de todos os B2BUnits).
 * - v18: Remove `b2bunit_addresses` — os endereços agora estão embutidos
 *        em `all_b2bunits[].addresses[]` (campo addressUsage + b2bUnitUID).
 * - v19: (migração intermediária)
 * - v20: Adiciona `order_outbox` — fonte de verdade para pedidos locais/offline.
 *        `order_history` passa a ser exclusivamente cache remoto (inbound sync).
 */
export const dbConfig: DBConfig = {
  name: 'StorefrontIDB',
  version: 23,
  objectStoresMeta: [
    // ── Vínculos de produto ──────────────────────────────────────────────────
    { store: 'product_category_links', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'product_mix_links', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'product_media_links', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },

    // ── Catálogo ─────────────────────────────────────────────────────────────
    {
      store: 'products',
      storeConfig: { keyPath: 'code', autoIncrement: false },
      storeSchema: [{ name: 'name', keypath: 'name', options: { unique: false } }],
    },
    { store: 'mixes', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'categories', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },

    // ── Todos os B2BUnits (dados completos + hierarquia) ──────────────────────
    // v17: substitui `b2b_unit_details` (CurrentB2BUnit) e `all_b2bunits_hierarchy`.
    //      keyPath: 'uid' — busca por UID do usuário logado via getByKey(uid).
    { store: 'all_b2bunits', storeConfig: { keyPath: 'uid', autoIncrement: false }, storeSchema: [] },

    // ── Dados de referência de pedido ────────────────────────────────────────
    { store: 'warehouses', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'payment_methods', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'payment_conditions', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'document_types', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'freight_types', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'storage_conditions', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'country_origins', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    { store: 'banks', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },

    // ── Tabelas de precificação (SAP) ─────────────────────────────────────────
    { store: 't685', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 't682i', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 't682z', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 't683s', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'a071', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'a073', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'a155', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'icms1', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'icms3', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'ipi1', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'ipi3', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'st3', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'pis', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'cofins', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },

    // ── Estoque ───────────────────────────────────────────────────────────────
    { store: 'stock_level', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'future_stock', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },

    // ── Regras de negócio ─────────────────────────────────────────────────────
    { store: 'delivery_routes', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'order_deadlines', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'minimum_order_rules', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'financial_titles', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
    { store: 'orders', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    {
      store: 'order_history',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'b2bUnitUid', keypath: 'b2bUnitUid', options: { unique: false } },
        { name: 'customerUid', keypath: 'customerUid', options: { unique: false } },
        { name: 'orderCode', keypath: 'orderCode', options: { unique: false } },
        { name: 'erpOrderCode', keypath: 'erpOrderCode', options: { unique: false } },
        { name: 'isQuotation', keypath: 'isQuotation', options: { unique: false } },
      ],
    },
    { store: 'quotas', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },

    // ── Promoções e cupons ────────────────────────────────────────────────────
    {
      store: 'promotions',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'code', keypath: 'code', options: { unique: true } },
        { name: 'active', keypath: 'active', options: { unique: false } },
      ],
    },
    {
      store: 'coupons',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [{ name: 'code', keypath: 'code', options: { unique: true } }],
    },

    // ── Carrinho ─────────────────────────────────────────────────────────────
    { store: 'cart', storeConfig: { keyPath: 'code', autoIncrement: false }, storeSchema: [] },
    // Note: order_outbox is the source of truth for local/offline orders.
    // order_history is exclusively a remote cache (populated by inbound sync).

    // ── Outbox de pedidos offline ─────────────────────────────────────────────
    {
      store: 'order_outbox',
      storeConfig: { keyPath: 'clientOrderId', autoIncrement: false },
      storeSchema: [
        { name: 'status', keypath: 'status', options: { unique: false } },
        { name: 'b2bUnitUid', keypath: 'b2bUnitUid', options: { unique: false } },
        { name: 'customerUid', keypath: 'customerUid', options: { unique: false } },
        { name: 'createdAt', keypath: 'createdAt', options: { unique: false } },
      ],
    },

    // ── Metadados ─────────────────────────────────────────────────────────────
    { store: 'sync_metadata', storeConfig: { keyPath: 'id', autoIncrement: false }, storeSchema: [] },
  ],
  migrationFactory: () => ({
    20: (db: IDBDatabase, transaction: IDBTransaction) => {
      // Criar order_outbox
      const outboxStore = db.createObjectStore('order_outbox', { keyPath: 'clientOrderId' });
      outboxStore.createIndex('status', 'status', { unique: false });
      outboxStore.createIndex('b2bUnitUid', 'b2bUnitUid', { unique: false });
      outboxStore.createIndex('customerUid', 'customerUid', { unique: false });
      outboxStore.createIndex('createdAt', 'createdAt', { unique: false });

      // Migrar pedidos locais legados de order_history para order_outbox
      if (db.objectStoreNames.contains('order_history')) {
        const historyStore = transaction.objectStore('order_history');
        historyStore.openCursor().onsuccess = (event: Event) => {
          const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;
          if (!cursor) return;
          const record = cursor.value;
          if (record.isLocalOrder && (record.localSyncStatus === 'PENDING' || record.localSyncStatus === 'ERROR')) {
            const outboxEntry = {
              clientOrderId: record.id,
              status: 'FAILED',
              request: {
                clientOrderId: record.id,
                b2bUnitUid: record.b2bUnitUid ?? '',
                customerUid: record.customerUid ?? '',
                isQuotation: record.isQuotation ?? false,
                entries: [],
              },
              display: {
                schemaVersion: 1,
                totalPrice: record.totalPrice ?? 0,
                totalTax: record.totalTax ?? 0,
                totalDiscounts: record.totalDiscounts ?? 0,
                ipi: record.ipi ?? 0,
                st: record.st ?? 0,
                icms: record.icms ?? 0,
                documentTypeDescription: record.documentTypeDescription ?? null,
                freightTypeDescription: record.freightTypeDescription ?? null,
                paymentMethodDescription: record.paymentMethodDescription ?? null,
                paymentConditionDescription: record.paymentConditionDescription ?? null,
                warehouseCode: record.entries?.[0]?.warehouseCode ?? null,
                entries: (record.entries ?? []).map((e: any) => ({
                  productCode: e.productCode ?? '',
                  productName: e.productName ?? '',
                  productEan: e.productEan ?? null,
                  qty: e.qty ?? 0,
                  basePrice: e.basePrice ?? 0,
                  totalPrice: e.totalPrice ?? 0,
                  ipi: e.ipi ?? 0,
                  st: e.st ?? 0,
                  icms: e.icms ?? 0,
                  warehouseCode: e.warehouseCode ?? null,
                  observation: e.observation ?? null,
                  imageUrls: e.imageUrls ?? [],
                  stockAllocations: [],
                })),
              },
              receipt: null,
              remoteObserved: false,
              attempts: 0,
              lastAttemptAt: null,
              lastError: 'Pedido legado (v19) — recriar se necessário',
              createdAt: record.createdAt ?? new Date().toISOString(),
              b2bUnitUid: record.b2bUnitUid ?? '',
              customerUid: record.customerUid ?? '',
            };
            outboxStore.add(outboxEntry);
          }
          cursor.continue();
        };
      }
    },
  }),
};
